/**
 * Dictation replacements — client-side dictionary of spoken words that get
 * substituted in the transcript before it lands in the report editor.
 *
 * Two layers work together:
 *
 *   1. Deepgram `replace=` params (see `deepgramReplaceParams` below).
 *      A fixed universal list that Deepgram applies server-side so the
 *      user sees punctuation in the LIVE interim transcript, not "comma
 *      comma comma" scrolling by. Hardcoded, not editable.
 *
 *   2. This user dictionary. Applied CLIENT-SIDE to the FINAL transcript
 *      only, on top of what Deepgram already did. Editable in Settings >
 *      Dictation. Persisted per-user (or per-clinic) via user_settings.
 *
 * Order: server-side happens first (interim transcripts already have
 * punctuation), then when the utterance is final we run this dictionary
 * over the whole line and insert into the editor.
 */
import { loadSettingsFull, saveUserSettings, SettingScope } from './userSettings';

export interface DictationReplacement {
  /** Stable local id (timestamp) - not needed on the server. */
  id: string;
  /** The word or phrase the radiologist says. Case-insensitive match. */
  spoken: string;
  /** The literal string to substitute in. Preserved verbatim, so users can
   *  set replacements with mixed case, punctuation, or short paragraphs. */
  replacement: string;
}

export function newReplacementId(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
}

/**
 * Encode newlines in a stored replacement value so they're visible when
 * loaded back into a single-line <input>. Without this, an entry like
 * "new paragraph" -> "\n\n" renders in Settings as an empty input and the
 * user has no way to see or edit it. We show "\n" / "\n\n" literally in
 * the UI; `decodeReplacementForStorage` reverses it on save.
 */
export function encodeReplacementForDisplay(s: string): string {
  return s.replace(/\r\n/g, '\n').replace(/\n/g, '\\n');
}

/**
 * Reverse of `encodeReplacementForDisplay`. Converts the literal two-char
 * sequence "\n" that users typed into the Settings input into an actual
 * newline character before saving. Users who want to insert a line break
 * via a dictation command just type `\n` in the replacement field.
 */
export function decodeReplacementForStorage(s: string): string {
  return s.replace(/\\n/g, '\n');
}

/**
 * UNIVERSAL punctuation / whitespace commands. ALWAYS applied, independent
 * of what the user has in their saved dictionary. Deepgram's dictation=true
 * handles some of these server-side, but its command list is incomplete
 * (notably no semicolon) AND the conversion is not 100% reliable
 * run-to-run. We mirror the common set client-side so punctuation works
 * even when Deepgram silently drops a conversion or when the user's saved
 * dictionary has been edited to remove entries.
 *
 * User's custom entries ALWAYS take precedence over universals (same
 * spoken token on both sides -> user's version wins). See
 * `mergeUniversals` below.
 */
export const UNIVERSAL_PUNCTUATION: DictationReplacement[] = [
  { id: 'u-comma',       spoken: 'comma',            replacement: ',' },
  { id: 'u-period',      spoken: 'period',           replacement: '.' },
  { id: 'u-fullstop',    spoken: 'full stop',        replacement: '.' },
  { id: 'u-colon',       spoken: 'colon',            replacement: ':' },
  { id: 'u-semicolon',   spoken: 'semicolon',        replacement: ';' },
  { id: 'u-semi-colon',  spoken: 'semi colon',       replacement: ';' },
  { id: 'u-qmark',       spoken: 'question mark',    replacement: '?' },
  { id: 'u-excl',        spoken: 'exclamation mark', replacement: '!' },
  { id: 'u-excl-point',  spoken: 'exclamation point',replacement: '!' },
  { id: 'u-hyphen',      spoken: 'hyphen',           replacement: '-' },
  { id: 'u-dash',        spoken: 'dash',             replacement: '-' },
  { id: 'u-slash',       spoken: 'slash',            replacement: '/' },
  { id: 'u-oparen',      spoken: 'open paren',       replacement: '(' },
  { id: 'u-cparen',      spoken: 'close paren',      replacement: ')' },
  { id: 'u-oparen-th',   spoken: 'open parenthesis', replacement: '(' },
  { id: 'u-cparen-th',   spoken: 'close parenthesis',replacement: ')' },
  { id: 'u-obrack',      spoken: 'open bracket',     replacement: '[' },
  { id: 'u-cbrack',      spoken: 'close bracket',    replacement: ']' },
  { id: 'u-newline',     spoken: 'new line',         replacement: '\n' },
  { id: 'u-nextline',    spoken: 'next line',        replacement: '\n' },
  { id: 'u-newpara',     spoken: 'new paragraph',    replacement: '\n\n' },
  { id: 'u-nextpara',    spoken: 'next paragraph',   replacement: '\n\n' },
];

/**
 * Merge the user's saved dictionary with UNIVERSAL_PUNCTUATION. The user's
 * entries take precedence on `spoken` collision (case-insensitive). This
 * is called every time dictation starts, so the universal baseline is
 * always available regardless of what's in the user's saved list.
 */
export function mergeUniversals(userEntries: DictationReplacement[]): DictationReplacement[] {
  const userSpokens = new Set(
    userEntries
      .map((e) => (e.spoken || '').trim().toLowerCase())
      .filter(Boolean),
  );
  const fallbacks = UNIVERSAL_PUNCTUATION.filter(
    (u) => !userSpokens.has(u.spoken.toLowerCase()),
  );
  // User entries first so applyReplacements processes them before falling
  // back to universals - lets a user override a universal with a different
  // substitution if they want.
  return [...userEntries, ...fallbacks];
}

/**
 * Default dictionary shipped out of the box. Users can edit / delete /
 * add to it via Settings > Dictation. Includes:
 *   - Universal punctuation ("comma", "period", "question mark", ...)
 *   - Newline commands ("new paragraph", "new line")
 *   - A couple of common radiology shortcuts (NAD, WNL)
 *
 * Everything below is editable. Note that UNIVERSAL_PUNCTUATION is ALSO
 * applied unconditionally (see mergeUniversals), so deleting an entry
 * here no longer silently breaks punctuation.
 */
export const DEFAULT_CLIENT_REPLACEMENTS: DictationReplacement[] = [
  { id: 'def-comma',      spoken: 'comma',            replacement: ',' },
  { id: 'def-period',     spoken: 'period',           replacement: '.' },
  { id: 'def-fullstop',   spoken: 'full stop',        replacement: '.' },
  { id: 'def-qmark',      spoken: 'question mark',    replacement: '?' },
  { id: 'def-excl',       spoken: 'exclamation mark', replacement: '!' },
  { id: 'def-colon',      spoken: 'colon',            replacement: ':' },
  { id: 'def-semi',       spoken: 'semicolon',        replacement: ';' },
  { id: 'def-oparen',     spoken: 'open paren',       replacement: '(' },
  { id: 'def-cparen',     spoken: 'close paren',      replacement: ')' },
  { id: 'def-obrack',     spoken: 'open bracket',     replacement: '(' },
  { id: 'def-cbrack',     spoken: 'close bracket',    replacement: ')' },
  { id: 'def-newpara',    spoken: 'new paragraph',    replacement: '\n\n' },
  { id: 'def-newline',    spoken: 'new line',         replacement: '\n' },
  { id: 'def-nextline',   spoken: 'next line',        replacement: '\n' },
  { id: 'def-nad',        spoken: 'NAD',              replacement: 'no acute distress' },
  { id: 'def-wnl',        spoken: 'WNL',              replacement: 'within normal limits' },
];

/**
 * Turn the user's dictionary into repeated `replace=find:sub` query pairs
 * for Deepgram's WebSocket URL. Applied SERVER-SIDE by Deepgram so live
 * interim transcripts already show the substitutions - users don't
 * watch "comma comma comma" scroll by before their sentence finalises.
 *
 * We only push entries whose replacement is a SHORT literal string
 * (not multi-line). Deepgram's `replace` param has a per-URL length
 * budget and doesn't handle newlines well; multi-line expansions like
 * "new paragraph" are handled client-side only, on the final transcript.
 */
export function deepgramReplaceParams(entries: DictationReplacement[]): Array<[string, string]> {
  const out: Array<[string, string]> = [];
  for (const e of entries) {
    const spoken = (e.spoken || '').trim();
    const sub = e.replacement || '';
    if (!spoken) continue;
    // Skip multi-line replacements - Deepgram would emit the literal
    // "\n" characters in the transcript rather than an actual newline.
    if (sub.includes('\n')) continue;
    // Deepgram's colon is the separator; if the spoken token contains
    // ":" the query would parse ambiguously. Simplest fix: skip.
    if (spoken.includes(':')) continue;
    out.push(['replace', `${spoken}:${sub}`]);
  }
  return out;
}

const SETTINGS_KEY = 'dictationReplacements';

export async function loadReplacements(): Promise<{
  replacements: DictationReplacement[];
  scope?: SettingScope;
}> {
  const s = await loadSettingsFull();
  const raw = (s.values as any)[SETTINGS_KEY];
  if (!raw) return { replacements: [], scope: undefined };
  try {
    const list = JSON.parse(raw);
    return {
      replacements: Array.isArray(list) ? list : [],
      scope: (s.scope as any)[SETTINGS_KEY],
    };
  } catch {
    return { replacements: [], scope: (s.scope as any)[SETTINGS_KEY] };
  }
}

export async function saveReplacements(
  replacements: DictationReplacement[],
  scope: SettingScope,
): Promise<boolean> {
  return saveUserSettings({ [SETTINGS_KEY]: JSON.stringify(replacements) } as any, scope);
}

/**
 * Apply the user's dictionary to a transcript.
 *
 *   - Case-insensitive match on the SPOKEN token.
 *   - Word-boundary (\b) based matching with Unicode support, so
 *     "period" as a word gets replaced but "periodic" is left alone.
 *   - Entries sorted LONGEST-FIRST so multi-word phrases like
 *     "semi colon" match before the overlapping single word "colon".
 *   - After all substitutions, tidies up whitespace before punctuation
 *     ("hello , world" becomes "hello, world") - a common artefact when
 *     Deepgram inserts a space around the spoken punctuation word.
 *
 * Empty entries (blank spoken field) are skipped defensively so a
 * half-typed row in Settings can't break dictation.
 */
export function applyReplacements(
  text: string,
  entries: DictationReplacement[],
): string {
  if (!text) return text;
  let out = text;
  // Longest first so "semi colon" beats "colon".
  const sorted = [...entries]
    .filter((e) => (e.spoken || '').trim().length > 0)
    .sort((a, b) => b.spoken.trim().length - a.spoken.trim().length);
  const applied: string[] = [];
  for (const e of sorted) {
    const needle = e.spoken.trim();
    const escaped = needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    // Plain word boundary. With the /u flag, \b correctly handles
    // Unicode letters. Simpler and more predictable than the
    // look-around-based matcher this file used to carry.
    const re = new RegExp(`\\b${escaped}\\b`, 'giu');
    const before = out;
    out = out.replace(re, e.replacement);
    if (out !== before) applied.push(`${needle}->${e.replacement.replace(/\n/g, '\\n')}`);
  }
  // Clean up any lingering "word , word" that Deepgram might have left
  // when it inserted spaces around a spoken punctuation word we then
  // replaced with the actual symbol.
  out = out.replace(/[ \t]+([.,!?;:])/g, '$1');
  // We intentionally do NOT force-capitalize the first letter here. An
  // earlier version did, but that broke continuations: user says "the
  // lung is clear" (final 1), pauses, says "and the heart is normal"
  // (final 2) — the second utterance is mid-paragraph and should stay
  // lowercase, but a blind force-cap turned it into "And the heart..."
  // which looked random to the user. Deepgram's smart_format handles
  // sentence-case at utterance boundaries well enough; let it decide.
  if (applied.length > 0) {
    try { console.log('[Dictate] applied: ' + applied.join(' | ')); } catch { /* ignore */ }
  }
  return out;
}
